import { Merchant } from '@frontend/api-payments';

export const MIXED = 'mixed';
type MIXED_VALUE = typeof MIXED;

export type MixedType<T> = T | MIXED_VALUE;
export type MixedBoolean = MixedType<boolean>;
export type MixedAccType<T> = { type: 'same' | MIXED_VALUE; value: T };

export const getMixedAccDefaultValue = <T>(value: T): MixedAccType<T> => ({ type: 'same', value });

export const getMixedAccValue = <T>(acc: MixedAccType<T>, value: T, idx: number): MixedAccType<T> =>
  idx === 0 || (acc.value === value && acc.type === 'same') ? { type: 'same', value } : { type: MIXED, value };

export const getMixedFinalValue = <T>(acc: MixedAccType<T>) => (acc.type === 'same' ? acc.value : MIXED);

export const getMixedValue = <T>(values: T[]): MixedType<T> => {
  const initialValue = values[0] ? getMixedAccDefaultValue(values[0]) : ({} as MixedAccType<T>);

  const acc = values.reduce((acc, value, idx) => getMixedAccValue(acc, value, idx), initialValue);
  return getMixedFinalValue(acc);
};

export const getMixedMerchantValues = <T extends { merchant?: Merchant }>(
  locationIds: string[],
  merchantsData: Record<string, T>
) => {
  const firstLocationId = locationIds[0] ?? '';
  const firstLocationMerchant = merchantsData[firstLocationId];
  const defaultMerchant = firstLocationMerchant?.merchant;

  const acc = locationIds.reduce(
    (acc, locationId, idx) => {
      acc.name = getMixedAccValue(acc.name, merchantsData[locationId]?.merchant?.name, idx);
      acc.email = getMixedAccValue(acc.email, merchantsData[locationId]?.merchant?.email, idx);
      acc.receiptEmail = getMixedAccValue(acc.receiptEmail, merchantsData[locationId]?.merchant?.receiptEmail, idx);
      acc.phoneNumber = getMixedAccValue(acc.phoneNumber, merchantsData[locationId]?.merchant?.phoneNumber, idx);
      acc.statementDescriptor = getMixedAccValue(
        acc.statementDescriptor,
        merchantsData[locationId]?.merchant?.statementDescriptor,
        idx
      );
      return acc;
    },
    {
      name: getMixedAccDefaultValue(defaultMerchant?.name),
      email: getMixedAccDefaultValue(defaultMerchant?.email),
      receiptEmail: getMixedAccDefaultValue(defaultMerchant?.receiptEmail),
      phoneNumber: getMixedAccDefaultValue(defaultMerchant?.phoneNumber),
      statementDescriptor: getMixedAccDefaultValue(defaultMerchant?.statementDescriptor),
    }
  );
  return {
    name: getMixedFinalValue(acc.name),
    email: getMixedFinalValue(acc.email),
    receiptEmail: getMixedFinalValue(acc.receiptEmail),
    phoneNumber: getMixedFinalValue(acc.phoneNumber),
    statementDescriptor: getMixedFinalValue(acc.statementDescriptor),
  };
};
